import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCourseWithLesson, fetchCoursesById, fetchLessonByCourseId, fetchPrerequisiteByCourse, fetchProcessListByprogramIdANDCourseId, fetchProgramList } from '../redux/actions/Actions'
import Prerequisite from './Prerequisite'
import Lessons from './Lessons'
import LessonCommonMessage from './LessonCommonMessage'
import PrerequisiteCommon from './PrerequisiteCommon'
import ProcessListCommon from './ProcessCommon'
import RequestCallback from '../RequestCallback'
import SEO from '../common/SEO'
import CourseProgramOverview, { JAVA_FULLSTACK_OVERVIEW } from './CourseProgramOverview'
import { FaJava, FaReact, FaGitAlt, FaHtml5, FaCss3Alt, FaPython, FaNodeJs, FaPhp, FaAws, FaLinux } from 'react-icons/fa'
import { VscAzure } from 'react-icons/vsc'
import { SiSpringboot, SiMysql, SiHibernate, SiJavascript, SiPostman, SiDotnet, SiCsharp, SiAngular, SiDjango, SiMongodb, SiTypescript, SiFlutter, SiKotlin, SiPostgresql, SiVuedotjs, SiLaravel, SiFlask, SiDocker, SiKubernetes, SiTerraform, SiAzuredevops, SiGithubactions, SiJenkins } from 'react-icons/si'

const HERO_CHECKS = [
  'Industry Mentors',
  'Live Project Internship',
  'Hands-on Training',
  'Mini Projects',
  'Capstone Project',
  'Internship Certificate',
]

const TECH_STRIP = [
  { name: 'Java',             color: '#E76F00', Icon: FaJava },
  { name: 'Spring Boot',      color: '#6DB33F', Icon: SiSpringboot },
  { name: 'Hibernate',        color: '#BCA136', Icon: SiHibernate },
  { name: 'MySQL',            color: '#4479A1', Icon: SiMysql },
  { name: 'HTML5',            color: '#E34C26', Icon: FaHtml5 },
  { name: 'CSS3',             color: '#264DE4', Icon: FaCss3Alt },
  { name: 'JavaScript (ES6)', color: '#F7DF1E', Icon: SiJavascript },
  { name: 'React.js',         color: '#61DAFB', Icon: FaReact },
  { name: 'Git & GitHub',     color: '#F05032', Icon: FaGitAlt },
  { name: 'Postman',          color: '#FF6C37', Icon: SiPostman },
]

// Extra icons matched by name when a course supplies its own technologies list
const TECH_ICONS = [
  ...TECH_STRIP,
  { name: 'AWS',              color: '#FF9900', Icon: FaAws },
  { name: 'Azure',            color: '#0078D4', Icon: VscAzure },
  { name: 'Azure DevOps',     color: '#0078D7', Icon: SiAzuredevops },
  { name: 'Python',           color: '#3776AB', Icon: FaPython },
  { name: 'Docker',           color: '#2496ED', Icon: SiDocker },
  { name: 'Kubernetes',       color: '#326CE5', Icon: SiKubernetes },
  { name: 'Terraform',        color: '#7B42BC', Icon: SiTerraform },
  { name: 'Linux',            color: '#333333', Icon: FaLinux },
  { name: 'GitHub Actions',   color: '#2088FF', Icon: SiGithubactions },
  { name: 'Jenkins',          color: '#D24939', Icon: SiJenkins },
]

// Exact name first, then the longest partial match (so "JavaScript" doesn't pick "Java")
const findTechIcon = (name) => {
  const n = name.toLowerCase().trim()
  const exact = TECH_ICONS.find(t => t.name.toLowerCase() === n)
  if (exact) return exact
  return TECH_ICONS
    .filter(t => { const tn = t.name.toLowerCase(); return tn.includes(n) || n.includes(tn) })
    .sort((a, b) => b.name.length - a.name.length)[0]
}

const PROG_MODES = [
  { label: 'Offline', desc: 'Classroom learning at our training center' },
  { label: 'Online',  desc: 'Live instructor-led virtual sessions' },
  { label: 'Hybrid',  desc: 'Combination of online + offline' },
]

const DURATIONS = [
  {
    title: '3 Months Internship', color: '#16A34A', colorDark: '#15803D',
    phases: [
      { name: 'Technical Training',  duration: '1 Month', items: ['Core Java', 'Database', 'Frontend', 'Hands-on Labs'] },
      { name: 'Mini Project',        duration: '1 Month', items: ['Requirement Analysis', 'Coding', 'Testing'] },
      { name: 'Internship Project',  duration: '1 Month', items: ['Team Work', 'Deployment', 'Presentation'] },
    ],
    deliverables: ['Internship Certificate', 'Project Certificate', 'GitHub Portfolio'],
  },
  {
    title: '6 Months Internship', color: '#7C3AED', colorDark: '#6D28D9',
    phases: [
      { name: 'Training',                duration: '2 Months', items: ['Core Java', 'Spring Boot', 'MySQL', 'Frontend'] },
      { name: 'Capstone Project',        duration: '1 Month',  items: ['Development', 'Testing', 'Documentation', 'Deployment'] },
      { name: 'Live Project Internship', duration: '3 Months', items: ['Daily Stand-ups', 'Sprint Planning', 'Jira Tasks', 'Client Demonstration'] },
    ],
    deliverables: ['Experience Letter', 'Live Project Certificate', 'Completion Certificate', 'Industry Portfolio'],
  },
]

const BADGE_POS = [
  { top: '6%',  left: '2%'  },
  { top: '2%',  left: '57%' },
  { top: '72%', left: '0%'  },
  { top: '70%', left: '57%' },
]

const applyPos = (badges) => badges.map((b, i) => ({ ...b, ...BADGE_POS[i] }))

const getCourseBadges = (name = '') => {
  const n = name.toLowerCase()
  if (n.includes('devops') || n.includes('cloud') || n.includes('aws') || n.includes('azure'))
    return applyPos([
      { text: 'AWS',        Icon: FaAws,        color: '#FF9900', bg: '#FFF7ED' },
      { text: 'Azure',      Icon: VscAzure,     color: '#0078D4', bg: '#EFF6FF' },
      { text: 'Docker',     Icon: SiDocker,     color: '#2496ED', bg: '#F0F9FF' },
      { text: 'Kubernetes', Icon: SiKubernetes, color: '#326CE5', bg: '#EFF3FF' },
    ])
  if (n.includes('c#') || n.includes('.net') || n.includes('dotnet') || n.includes('csharp'))
    return applyPos([
      { text: 'C#',      Icon: SiCsharp,     color: '#68217A', bg: '#F9F0FF' },
      { text: '.NET',    Icon: SiDotnet,     color: '#512BD4', bg: '#EDE9FF' },
      { text: 'MySQL',   Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
      { text: 'Angular', Icon: SiAngular,    color: '#DD0031', bg: '#FFF1F1' },
    ])
  if (n.includes('python') || n.includes('django') || n.includes('flask'))
    return applyPos([
      { text: 'Python',  Icon: FaPython,     color: '#3776AB', bg: '#EFF6FF' },
      { text: 'Django',  Icon: SiDjango,     color: '#092E20', bg: '#F0FDF4' },
      { text: 'MySQL',   Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
      { text: 'React',   Icon: FaReact,      color: '#61DAFB', bg: '#F0F9FF' },
    ])
  if (n.includes('mern') || n.includes('node') || n.includes('mongodb'))
    return applyPos([
      { text: 'MongoDB', Icon: SiMongodb,    color: '#47A248', bg: '#F0FDF4' },
      { text: 'Node.js', Icon: FaNodeJs,     color: '#339933', bg: '#F0FDF4' },
      { text: 'React',   Icon: FaReact,      color: '#61DAFB', bg: '#F0F9FF' },
      { text: 'Express', Icon: SiJavascript, color: '#F7DF1E', bg: '#FEFCE8' },
    ])
  if (n.includes('angular') || n.includes('typescript'))
    return applyPos([
      { text: 'Angular',     Icon: SiAngular,    color: '#DD0031', bg: '#FFF1F1' },
      { text: 'TypeScript',  Icon: SiTypescript, color: '#3178C6', bg: '#EFF6FF' },
      { text: 'Node.js',     Icon: FaNodeJs,     color: '#339933', bg: '#F0FDF4' },
      { text: 'MySQL',       Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
    ])
  if (n.includes('flutter') || n.includes('kotlin') || n.includes('android') || n.includes('mobile'))
    return applyPos([
      { text: 'Flutter',  Icon: SiFlutter,    color: '#02569B', bg: '#EFF6FF' },
      { text: 'Kotlin',   Icon: SiKotlin,     color: '#7F52FF', bg: '#F5F0FF' },
      { text: 'Firebase', Icon: SiPostgresql, color: '#FFCA28', bg: '#FEFCE8' },
      { text: 'MySQL',    Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
    ])
  if (n.includes('php') || n.includes('laravel'))
    return applyPos([
      { text: 'PHP',     Icon: FaPhp,        color: '#777BB4', bg: '#F5F3FF' },
      { text: 'Laravel', Icon: SiLaravel,    color: '#FF2D20', bg: '#FFF1F0' },
      { text: 'MySQL',   Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
      { text: 'Vue.js',  Icon: SiVuedotjs,   color: '#42B883', bg: '#F0FDF4' },
    ])
  if (n.includes('frontend') || n.includes('react') || n.includes('ui'))
    return applyPos([
      { text: 'React',      Icon: FaReact,      color: '#61DAFB', bg: '#F0F9FF' },
      { text: 'JavaScript', Icon: SiJavascript, color: '#F7DF1E', bg: '#FEFCE8' },
      { text: 'HTML5',      Icon: FaHtml5,      color: '#E34C26', bg: '#FFF4F0' },
      { text: 'CSS3',       Icon: FaCss3Alt,    color: '#264DE4', bg: '#EFF3FF' },
    ])
  // Default — Java Full Stack
  return applyPos([
    { text: 'Java',        Icon: FaJava,       color: '#E76F00', bg: '#FFF7ED' },
    { text: 'Spring Boot', Icon: SiSpringboot, color: '#6DB33F', bg: '#F0FDF4' },
    { text: 'MySQL',       Icon: SiMysql,      color: '#4479A1', bg: '#EFF6FF' },
    { text: 'React',       Icon: FaReact,      color: '#61DAFB', bg: '#F0F9FF' },
  ])
}

const WHO_SHOULD_ENROLL = [
  { icon: '🎓', label: 'Engineering Students' },
  { icon: '📚', label: 'Degree Students' },
  { icon: '📋', label: 'Diploma Students' },
  { icon: '👨‍💼', label: 'Fresh Graduates' },
  { icon: '🔍', label: 'Job Seekers' },
  { icon: '🔄', label: 'Career Transition Professionals' },
  { icon: '💼', label: 'Working Professionals' },
]

const OUTCOMES = [
  'Develop Industry-Grade Applications',
  'Work with Modern Technologies & Frameworks',
  'Participate in Agile Development Teams',
  'Build Production-Ready Solutions',
  'Face Technical Interviews Confidently',
]

const CourseDetail = (props) => {
  const { id }       = useParams()
  const location     = useLocation()
  const navigate     = useNavigate()
  const dispatch     = useDispatch()
  const [showApply, setShowApply] = useState(false)
  const [pendingDownload, setPendingDownload] = useState(false)

  const programInterestedFromState = location.state?.interestedProgram   || ''
  const programInterestedId = location.state?.interestedProgramId || null
  const Program_id          = location.state?.Program_id          || null

  const programs                   = useSelector((s) => s.fetchAllPrograms.programs) || []
  const fetchCourseById            = useSelector((s) => s.fetchCourseById.courseById)
  const fetchCourseWithLessonNunit = useSelector((s) => s.getCourseWithLesson.courseWithLesson)
  const prerequisitesByCourse      = useSelector((s) => s.getPrerequisiteByCourse.prerequisiteByCourse)
  const lessonsByCourseId          = useSelector((s) => s.getLessonsByCourseId.lessonsByCourse)

  const selectedCourse     = fetchCourseById[0]
  const interestedCourse   = selectedCourse?.course_name
  const interestedCourseId = selectedCourse?.course_id
  const heroBadges         = getCourseBadges(selectedCourse?.course_name)

  const programInterested = programInterestedFromState
    || (selectedCourse?.program_id && programs.find(p => p.program_id === selectedCourse.program_id)?.program_name)
    || ''

  const cd = (() => {
    try {
      const raw = selectedCourse?.course_details
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw || {})
      return parsed
    } catch { return {} }
  })()

  useEffect(() => {
    dispatch(fetchCoursesById(id))
    dispatch(fetchCourseWithLesson(id))
    dispatch(fetchPrerequisiteByCourse(id))
    dispatch(fetchLessonByCourseId(id))
    dispatch(fetchProgramList())
    dispatch(fetchProcessListByprogramIdANDCourseId(programInterestedId, selectedCourse?.course_id))
    props.setBgColor('')
    return () => props.setBgColor('')
  }, [dispatch, id])

  const onDownload = () => {
    setPendingDownload(true)
    setShowApply(true)
  }

  const handleApplySuccess = () => {
    if (pendingDownload) {
      setPendingDownload(false)
      const derivedProgramId = Program_id || selectedCourse?.program_id || null
      navigate('/download', {
        state: { interestedCourseId, programInterestedId, programInterested, interestedCourse, Program_id: derivedProgramId }
      })
    }
  }

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", background: '#F8FAFC', minHeight: '100vh' }}>
      <SEO
        title={interestedCourse || 'Course Details'}
        path={`/courses/${id}`}
        description={`Learn ${interestedCourse || 'this course'} with UPL Snipe — industry mentors, hands-on training, live project internship, and placement support. Enroll now!`}
        keywords={`${interestedCourse || ''}, tech course, software training, coding course, IT training, UPL Snipe course`}
      />
      <style>{`
        #cd-hero { background: linear-gradient(135deg, #0A0F2E 0%, #101B4A 50%, #0E1A3A 100%) !important; }
        #cd-cta  { background: linear-gradient(135deg, #0B1A3E 0%, #1E3A8A 100%) !important; }

        /* ── Tablet (≤1000px) ── */
        @media(max-width:1000px) {
          #cd-hero-grid   { grid-template-columns: 1fr !important; }
          #cd-hero-right  { display: none !important; }
          #cd-dur-grid    { grid-template-columns: 1fr !important; }
          #cd-learn-grid  { grid-template-columns: repeat(3,1fr) !important; }
          #cd-proj-grid   { grid-template-columns: 1fr !important; }
          #cd-hl-grid     { grid-template-columns: repeat(3,1fr) !important; }
          #cd-why-grid    { grid-template-columns: 1fr !important; }
          #cd-stats-grid  { grid-template-columns: repeat(2,1fr) !important; }
          #cd-test-grid   { grid-template-columns: repeat(2,1fr) !important; }
          #cd-cta-grid    { grid-template-columns: 1fr !important; text-align:center !important; }
          #cd-cta-icon    { display: none !important; }
          #cd-cta-btns    { justify-content:center !important; }
          #cd-infobar     { flex-wrap: wrap !important; }
        }

        /* ── Large mobile (≤700px) ── */
        @media(max-width:700px) {
          #cd-learn-grid  { grid-template-columns: repeat(2,1fr) !important; }
          #cd-hl-grid     { grid-template-columns: repeat(2,1fr) !important; }
          #cd-test-grid   { grid-template-columns: 1fr !important; }
          #cd-ps-steps    { flex-wrap:wrap !important; justify-content:center !important; gap:16px !important; }
          #cd-pill-strip  { flex-wrap:wrap !important; border-radius:14px !important; padding:10px 14px !important; gap:6px !important; }
          #cd-infobar     { flex-direction:column !important; }
          #cd-cta-btns    { flex-direction:column !important; width:100% !important; }
          #cd-cta-btns button { width:100% !important; justify-content:center !important; }
          #cd-dur-grid    { grid-template-columns: 1fr !important; }
        }

        /* ── Small mobile (≤480px) ── */
        @media(max-width:480px) {
          #cd-learn-grid  { grid-template-columns: 1fr !important; }
          #cd-stats-grid  { grid-template-columns: 1fr !important; }
          #cd-hl-grid     { grid-template-columns: 1fr !important; }
          #cd-tech-row    { flex-direction: column !important; align-items:flex-start !important; }
          #cd-hero-btns   { flex-direction:column !important; }
          #cd-hero-btns button, #cd-hero-btns a { width:100% !important; text-align:center !important; justify-content:center !important; }
        }
      `}</style>

      {/* ══ HERO ══ */}
      <section id="cd-hero" style={{ padding: '44px 5% 52px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-10%', right: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(37,99,235,0.18) 0%,transparent 65%)', pointerEvents: 'none' }} />

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'rgba(255,255,255,0.4)', marginBottom: 28, flexWrap: 'wrap', position: 'relative', zIndex: 2 }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Home</Link>
          <span>›</span>
          <Link to="/programs" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>Programs</Link>
          {programInterested && <>
            <span>›</span>
            <span style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }} onClick={() => navigate(-1)}>{programInterested}</span>
          </>}
          <span>›</span>
          <span style={{ color: '#fff', fontWeight: 700 }}>{selectedCourse?.course_name || '…'}</span>
        </div>

        <div id="cd-hero-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 48, alignItems: 'center', position: 'relative', zIndex: 2 }}>

          {/* LEFT */}
          <div>
            <h1 style={{ fontSize: 'clamp(28px,3.8vw,50px)', fontWeight: 900, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
              {selectedCourse?.course_name || 'Loading…'}
            </h1>

            {/* Pill strip */}
            <div id="cd-pill-strip" style={{ display: 'inline-flex', alignItems: 'center', background: 'rgba(37,99,235,0.2)', border: '1.5px solid rgba(37,99,235,0.5)', borderRadius: 999, padding: '7px 20px', marginBottom: 16 }}>
              {(cd.hero_pills || ['3 Months', '6 Months', 'Offline', 'Online', 'Hybrid']).map((t, i, a) => (
                <React.Fragment key={t}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{t}</span>
                  {i < a.length - 1 && <span style={{ color: 'rgba(255,255,255,0.35)', margin: '0 10px' }}>|</span>}
                </React.Fragment>
              ))}
            </div>

            {/* 2-col checklist */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 28 }}>
              {(cd.hero_checks || HERO_CHECKS).map(c => (
                <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="11" fill="rgba(34,197,94,0.18)" stroke="#22C55E" strokeWidth="1.5" />
                    <polyline points="7 12 10 15 17 9" stroke="#22C55E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {c}
                </div>
              ))}
            </div>

            {/* 4 buttons */}
            <div id="cd-hero-btns" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setShowApply(true)} style={{ padding: '12px 22px', background: '#F59E0B', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 4px 16px rgba(245,158,11,0.4)' }}>
                Apply Now
              </button>
              <button onClick={onDownload} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 18px', background: 'rgba(255,255,255,0.07)', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                Download Brochure
              </button>
              <button onClick={() => window.dispatchEvent(new CustomEvent('openApplyModal'))} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 18px', background: 'rgba(255,255,255,0.07)', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                Talk to Adviser
              </button>
            </div>
          </div>

          {/* RIGHT — tech orbit visual */}
          <div id="cd-hero-right" style={{ position: 'relative', height: 400 }}>
            {/* Glow */}
            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 440, height: 440, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 65%)', pointerEvents: 'none' }} />
            {/* Outer ring */}
            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 360, height: 360, borderRadius: '50%', border: '1px solid rgba(99,179,237,0.16)', background: 'transparent', pointerEvents: 'none' }} />
            {/* Middle ring */}
            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 256, height: 256, borderRadius: '50%', border: '1.5px solid rgba(99,179,237,0.26)', background: 'rgba(37,99,235,0.06)', pointerEvents: 'none' }} />
            {/* Inner circle + code icon */}
            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: 148, height: 148, borderRadius: '50%', background: 'rgba(37,99,235,0.22)', border: '2px solid rgba(99,179,237,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
              </svg>
            </div>
            {/* Dynamic tech badges */}
            {heroBadges.map(b => (
              <div key={b.text} style={{ position: 'absolute', top: b.top, left: b.left, display: 'flex', alignItems: 'center', gap: 10, background: b.bg, border: `1.5px solid ${b.color}28`, borderRadius: 14, padding: '11px 18px', boxShadow: '0 6px 28px rgba(0,0,0,0.36)', whiteSpace: 'nowrap' }}>
                <b.Icon size={24} color={b.color} />
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1E293B' }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ INFO BAR ══ */}
      <section style={{ background: 'linear-gradient(135deg,#0A0F2E 0%,#101B4A 100%)', padding: '0 5%', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div id="cd-infobar" style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
          {[
            ...(cd.info_bar || [
              { title:'Program Mode', text:'Online | Offline | Hybrid' },
              { title:'Eligibility', text:'Any Graduate / Final Year Students' },
              { title:'Batch Size', text:'Small Batches for Individual Attention' },
              { title:'Certificate', text:'UPL Completion Certificate' },
              { title:'Tools & LMS', text:'Industry Standard Tools & LMS Access' },
              { title:'Mock Tests & Interviews', text:'Regular Assessments, Mocks & Feedback' },
            ]).map(b => ({ label: b.title, value: b.text, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l2 2"/></svg> })),
          ].map((item, i, arr) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 20px', flex: 1, minWidth: 160, borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
              <div style={{ color: '#60A5FA', flexShrink: 0 }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#93C5FD', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{item.label}</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', fontWeight: 500, marginTop: 2, lineHeight: 1.4 }}>{item.value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ WHAT YOU WILL LEARN ══ */}
      <section style={{ background: '#fff', padding: '52px 5%', borderBottom: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 36 }}>
            <div style={{ flex: 1, height: 2, background: 'linear-gradient(to right,transparent,#2563EB)', maxWidth: 120, borderRadius: 2 }} />
            <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '0.08em', textTransform: 'uppercase' }}>What You Will Learn</h2>
            <div style={{ flex: 1, height: 2, background: 'linear-gradient(to left,transparent,#2563EB)', maxWidth: 120, borderRadius: 2 }} />
          </div>
          <div id="cd-learn-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cd.learn_topics?.length || 7}, minmax(0, 1fr))`, gap: 12 }}>
            {(cd.learn_topics || [
              { title:'Core Java', color:'#E76F00', items:['Java Basics','OOPs Concepts','Collections','Exception Handling','Multithreading','Java 8 Features'] },
              { title:'Web Technologies', color:'#0D9488', items:['HTML5','CSS3','JavaScript ES6','Bootstrap 5','jQuery'] },
              { title:'Frontend Framework', color:'#EA580C', items:['React JS','Components','Props & State','React Router','Hooks','Redux (Basics)'] },
              { title:'Backend Development', color:'#F59E0B', items:['Servlets & JSP','RESTful Web Services','Spring Core','Spring MVC','Spring Boot','Spring Security'] },
              { title:'Database', color:'#DC2626', items:['MySQL','JDBC','Hibernate / JPA','SQL Queries','Database Design'] },
              { title:'Tools & Others', color:'#2563EB', items:['Git & GitHub','Maven','Postman','Swagger','Docker (Basics)','JWT, Logging'] },
              { title:'Real World & DevOps', color:'#1D4ED8', items:['AWS Basics','CI/CD Pipeline','Deployment','Agile / Scrum','Performance Optimization'] },
            ]).map((topic, idx) => ({ ...topic, num: idx + 1, label: topic.title })).map(topic => (
              <div key={topic.num} style={{ border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '18px 12px', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${topic.color}15`, border: `1.5px solid ${topic.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 18, fontWeight: 900, color: topic.color }}>{topic.num}</span>
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: topic.color, textAlign: 'center', lineHeight: 1.3 }}>{topic.label}</div>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 auto', width: 'fit-content', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {topic.items.map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: '#475569', lineHeight: 1.4 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={topic.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TECHNOLOGIES STRIP ══ */}
      <section style={{ background: '#fff', borderBottom: '1px solid #E2E8F0', padding: '22px 5%' }}>
        <div id="cd-tech-row" style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 18, fontWeight: 900, color: '#2563EB', margin: 0, lineHeight: 1.3, minWidth: 170, flexShrink: 0 }}>
            Technologies<br />You Will Learn
          </h3>
          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
            {(cd.technologies ? cd.technologies.map(name => {
              const match = findTechIcon(name);
              return match ? { ...match, name } : { name, color: '#475569', Icon: null };
            }) : TECH_STRIP).map(t => (
              <div key={t.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                {t.Icon ? <t.Icon size={30} color={t.color} /> : <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#475569' }}>{t.name[0]}</div>}
                <span style={{ fontSize: 10.5, fontWeight: 600, color: '#475569', textAlign: 'center', lineHeight: 1.2, maxWidth: 70 }}>{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROGRAM MODES + DURATION PHASES ══ */}
      <section style={{ background: '#F8FAFC', padding: '52px 5%', borderTop: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ fontSize: 26, fontWeight: 900, color: '#1E293B', textAlign: 'center', marginBottom: 32, letterSpacing: '-0.01em' }}>
            Program Duration
          </h2>
          <div id="cd-dur-grid" style={{ display: 'grid', gridTemplateColumns: '210px 1fr 1fr', gap: 20, alignItems: 'stretch' }}>

            {/* Program Modes */}
            <div style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, padding: '20px 16px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ fontSize: 15, fontWeight: 900, color: '#2563EB', margin: '0 0 0', textAlign: 'center', letterSpacing: '0.02em' }}>Program Modes</h3>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
              {PROG_MODES.map((m, i) => (
                <div key={m.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, justifyContent: 'center' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {i === 0 && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /></svg>}
                    {i === 1 && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>}
                    {i === 2 && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>}
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: '#2563EB', marginBottom: 3 }}>{m.label}</div>
                    <div style={{ fontSize: 11.5, color: '#64748B', lineHeight: 1.4 }}>{m.desc}</div>
                  </div>
                </div>
              ))}
              </div>
            </div>

            {/* 3M and 6M duration cards */}
            {/* course_details.phase1_items = [[3-month Phase 1 items], [6-month Phase 1 items]] overrides the defaults */}
            {DURATIONS.map((dur, di) => {
              const p1 = cd.phase1_items?.[di]
              return p1?.length ? { ...dur, phases: dur.phases.map((p, pi) => pi === 0 ? { ...p, items: p1 } : p) } : dur
            }).map(dur => (
              <div key={dur.title} style={{ background: '#fff', border: `1.5px solid ${dur.color}25`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ background: `linear-gradient(135deg, ${dur.color}, ${dur.colorDark})`, padding: '14px 18px', textAlign: 'center' }}>
                  <h3 style={{ color: '#fff', margin: 0, fontWeight: 900, fontSize: 16 }}>{dur.title}</h3>
                </div>
                <div style={{ display: 'flex', padding: '20px 14px 16px', alignItems: 'stretch' }}>
                  {dur.phases.map((p, pi) => (
                    <React.Fragment key={p.name}>
                      <div style={{ flex: 1, textAlign: 'center', padding: '0 8px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ fontWeight: 800, fontSize: 12, color: dur.color, marginBottom: 4, letterSpacing: '0.05em' }}>Phase {pi + 1}</div>
                        <div style={{ fontWeight: 800, fontSize: 13, color: '#1E293B', marginBottom: 3, lineHeight: 1.3, minHeight: 40, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, marginBottom: 12 }}>({p.duration})</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left', flex: 1 }}>
                          {p.items.map(it => (
                            <li key={it} style={{ fontSize: 12, color: '#374151', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                              <span style={{ color: dur.color, flexShrink: 0, lineHeight: 1.6 }}>•</span>{it}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {pi < dur.phases.length - 1 && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 52, flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={dur.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px 14px', borderTop: `1px solid ${dur.color}20`, background: `${dur.color}06` }}>
                  {dur.deliverables.map(d => (
                    <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 5, background: `${dur.color}18`, color: dur.color, borderRadius: 999, padding: '4px 10px', fontSize: 11, fontWeight: 700 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                      {d}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══ LIVE PROJECTS + PROGRAM HIGHLIGHTS ══ */}
      <section style={{ background: '#EEF2FF', padding: '52px 5%', borderTop: '1px solid #C7D2FE' }}>
        <div id="cd-proj-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

          {/* LIVE PROJECTS */}
          <div style={{ background: '#fff', border: '1.5px solid #DBEAFE', borderRadius: 18, padding: '28px 30px', display: 'flex', flexDirection: 'column' }}>
            <h2 style={{ fontSize: 15, fontWeight: 900, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 22px' }}>Live Projects You Will Build</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center', flex: 1 }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(cd.live_projects || ['E-Commerce Web Application','Online Banking System','Employee Management System','Hospital Management System','Social Media Application','Task Management System']).map(p => (
                  <li key={p} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: '#1E293B', fontWeight: 500 }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" fill="#EFF6FF" stroke="#2563EB" strokeWidth="1.5"/><polyline points="9 12 11 14 15 10" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {p}
                  </li>
                ))}
              </ul>
              <div style={{ width: 140, height: 140, background: 'linear-gradient(135deg,#1E3A8A 0%,#2563EB 100%)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 8px 32px rgba(37,99,235,0.25)' }}>
                <svg width="92" height="72" viewBox="0 0 92 72" fill="none">
                  <rect x="3" y="6" width="86" height="54" rx="5" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2"/>
                  <rect x="9" y="12" width="44" height="5" rx="2" fill="rgba(255,255,255,0.45)"/>
                  <rect x="9" y="22" width="36" height="30" rx="3" fill="rgba(255,255,255,0.12)"/>
                  <rect x="50" y="22" width="33" height="13" rx="3" fill="rgba(255,255,255,0.18)"/>
                  <rect x="50" y="39" width="33" height="13" rx="3" fill="rgba(255,255,255,0.12)"/>
                  <rect x="9" y="57" width="74" height="5" rx="2" fill="rgba(255,255,255,0.35)"/>
                </svg>
              </div>
            </div>
          </div>

          {/* PROGRAM HIGHLIGHTS */}
          <div style={{ background: '#fff', border: '1.5px solid #DBEAFE', borderRadius: 18, padding: '28px 30px' }}>
            <h2 style={{ fontSize: 15, fontWeight: 900, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 22px' }}>Program Highlights</h2>
            <div id="cd-hl-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {(cd.highlights || ['Hands-on Live Projects','Real-time Industry Use Cases','Code Reviews & Mentorship','Agile & Scrum Methodology','Resume & LinkedIn Profile Building']).map((label, i) => {
                const colors = ['#2563EB','#7C3AED','#0D9488','#EA580C','#DC2626'];
                const bgs    = ['#EFF6FF','#F5F3FF','#F0FDFA','#FFF7ED','#FEF2F2'];
                return { color: colors[i % colors.length], bg: bgs[i % bgs.length], label };
              }).map(h => (
                <div key={h.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '18px 10px', background: h.bg, border: `1.5px solid ${h.color}25`, borderRadius: 13 }}>
                  <div style={{ color: h.color }}>{h.icon}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0F172A', textAlign: 'center', lineHeight: 1.4 }}>{h.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHY CHOOSE UPL + PLACEMENT SUPPORT + LEARNING METHOD ══ */}
      <section style={{ background: '#fff', padding: '52px 5%', borderTop: '1px solid #E2E8F0' }}>
        <div id="cd-why-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '220px 1fr 220px', gap: 24, alignItems: 'start' }}>

          {/* WHY CHOOSE UPL */}
          <div style={{ background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 16, padding: '24px 20px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 900, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 18px' }}>Why Choose UPL?</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
              {['Industry-focused Curriculum','Experienced Corporate Mentors','Practical Learning Approach','Personalized Guidance','Lifetime LMS Access','100% Placement Assistance Support'].map(item => (
                <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, color: '#1E293B', fontWeight: 500, lineHeight: 1.4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* PLACEMENT SUPPORT (center - dark card) */}
          <div style={{ background: 'linear-gradient(135deg,#0B1A3E 0%,#1E3A8A 100%)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 8px 40px rgba(30,58,138,0.22)' }}>
            <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ fontSize: 16, fontWeight: 900, color: '#fff', textAlign: 'center', margin: 0, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Placement Support</h3>
            </div>
            <div style={{ padding: '22px 20px' }}>
              <div id="cd-ps-steps" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
                {[
                  { icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>, label:'Aptitude & Technical Training' },
                  { icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>, label:'Mock Tests & Coding Challenges' },
                  { icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>, label:'Mock Interviews & GD' },
                  { icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, label:'Resume Building & Profile Optimization' },
                  { icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>, label:'Campus Drive & Job Referrals' },
                ].map((step, i, arr) => (
                  <div key={step.label} style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: 1 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#93C5FD' }}>{step.icon}</div>
                      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.82)', textAlign: 'center', fontWeight: 600, lineHeight: 1.4 }}>{step.label}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <div style={{ paddingTop: 13, color: '#60A5FA', flexShrink: 0, fontSize: 16, fontWeight: 700 }}>›</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Placement Partner */}
            <div style={{ margin: '0 16px 16px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#93C5FD', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10, textAlign: 'center' }}>Our Placement Partner</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <a href="https://campusora.in/" target="_blank" rel="noopener noreferrer" style={{ background: '#fff', borderRadius: 10, padding: '8px 18px', fontWeight: 900, fontSize: 18, color: '#2563EB', letterSpacing: '-0.02em', flexShrink: 0, textDecoration: 'none', display: 'inline-block' }}>campus<span style={{ color: '#F59E0B' }}>ora</span></a>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  {['1000+ Hiring Partners','Smart Matching & Job Recommendations','End-to-End Placement Support'].map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* LEARNING METHOD */}
          <div style={{ background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 16, padding: '24px 20px' }}>
            <h3 style={{ fontSize: 15, fontWeight: 900, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 18px' }}>Learning Method</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                { step:'Learn',    sub:'Conceptual Clarity',          color:'#2563EB' },
                { step:'Build',    sub:'Real-world Applications',     color:'#7C3AED' },
                { step:'Practice', sub:'Assignments & Projects',      color:'#0D9488' },
                { step:'Review',   sub:'Feedback & Improvement',      color:'#EA580C' },
                { step:'Excel',    sub:'Industry Ready Professional', color:'#16A34A' },
              ].map((m, i, arr) => (
                <div key={m.step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, position: 'relative', paddingBottom: i < arr.length - 1 ? 16 : 0 }}>
                  {i < arr.length - 1 && <div style={{ position: 'absolute', left: 14, top: 32, bottom: 0, width: 2, background: `${m.color}30`, zIndex: 0 }} />}
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${m.color}15`, border: `2px solid ${m.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={m.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: m.color }}>{m.step}</div>
                    <div style={{ fontSize: 11.5, color: '#64748B', fontWeight: 500 }}>{m.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ STATS BAR ══ */}
      <section style={{ background: 'linear-gradient(135deg,#0B1A3E 0%,#1E3A8A 100%)', padding: '0 5%' }}>
        <div id="cd-stats-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)' }}>
          {(cd.stats || [
            { value:'10,000+', label:'Students Trained' },
            { value:'250+', label:'Hiring Partners' },
            { value:'500+', label:'Live Projects' },
            { value:'95%+', label:'Placement Rate' },
          ]).map(s => ({ ...s, icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> })).map((s, i, arr) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '28px 24px', borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
              <div style={{ color: '#60A5FA', flexShrink: 0 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section style={{ background: '#F8FAFC', padding: '56px 5%', borderTop: '1px solid #E2E8F0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#0F172A', textAlign: 'center', margin: '0 0 36px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>What Our Students Say</h2>
          <div id="cd-test-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 22 }}>
            {[
              { name:'Rahul Sharma',  stars:5, placed:'TCS',       text:"UPL's Java Full Stack program helped me go from zero to hero. The mentors and live projects made all the difference!" },
              { name:'Sneha Patel',   stars:5, placed:'Infosys',   text:'Best coding bootcamp experience! The curriculum is industry-ready and placement support is amazing.' },
              { name:'Vikram Reddy', stars:4, placed:'Capgemini', text:'The practical sessions, mock interviews and mentorship made me confident to crack my dream job.' },
            ].map(t => (
              <div key={t.name} style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 16, padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#2563EB,#7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800, flexShrink: 0 }}>
                    {t.name[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }}>{t.name}</div>
                    <div style={{ display: 'flex', gap: 3, marginTop: 3 }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill={i < t.stars ? '#F59E0B' : 'none'} stroke="#F59E0B" strokeWidth="1.8"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      ))}
                    </div>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 4, borderTop: '1px solid #F1F5F9', marginTop: 'auto' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#16A34A' }}>Placed at {t.placed}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ background: 'linear-gradient(135deg,#0B1A3E 0%,#1E3A8A 100%)', padding: '52px 5%', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle,rgba(37,99,235,0.25) 0%,transparent 65%)', pointerEvents: 'none' }} />
        <div id="cd-cta-grid" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 32, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div id="cd-cta-icon" style={{ width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="#60A5FA"/><circle cx="15" cy="9" r="1" fill="#60A5FA"/></svg>
          </div>
          <div>
            <h2 style={{ fontSize: 'clamp(20px,2.8vw,30px)', fontWeight: 900, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Ready to Start Your Dream Coding Journey?</h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', margin: 0 }}>Join our <strong style={{ color: '#93C5FD' }}>{selectedCourse?.course_name || 'program'}</strong> and transform your career with industry-ready skills.</p>
          </div>
          <div id="cd-cta-btns" style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
            <button onClick={() => setShowApply(true)} style={{ padding: '13px 28px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(37,99,235,0.45)', whiteSpace: 'nowrap' }}>Enroll Now</button>
            <button style={{ padding: '12px 24px', background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
              Talk to Adviser
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </button>
          </div>
        </div>
      </section>

      {showApply && (
        <RequestCallback
          isModal
          onClose={() => { setShowApply(false); setPendingDownload(false); }}
          onSuccess={handleApplySuccess}
          defaultProgram={programInterested}
          defaultCourse={interestedCourse || ''}
        />
      )}
    </div>
  )
}

export default CourseDetail
